import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, PLAN_LABELS } from "../lib/config";
import { Sparkles, CreditCard, Trophy, X, Heart } from "lucide-react";

// Helper to format relative time (es)
function relativeTime(iso) {
  if (!iso) return "";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return "hace instantes";
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} días`;
}

const ORDINALS_ES = [
  "primer", "segundo", "tercer", "cuarto", "quinto",
  "sexto", "séptimo", "octavo", "noveno", "décimo",
];

function ordinalEs(n) {
  if (n >= 1 && n <= 10) return ORDINALS_ES[n - 1];
  return `${n}°`;
}

function buildLabel(a) {
  if (a.type === "registration") {
    return {
      title: `${a.name} se acaba de inscribir`,
      sub: `Plan ${PLAN_LABELS[a.plan_type] || ""}`,
      icon: Sparkles,
      color: "from-orange-500 to-red-500",
    };
  }
  if (a.type === "payment") {
    const ord = ordinalEs(a.installment_number);
    return {
      title: `${a.name} pagó su ${ord} pago`,
      sub: `$${a.amount} · Cuota ${a.installment_number}/${a.total_installments}`,
      icon: CreditCard,
      color: "from-yellow-400 to-orange-500",
    };
  }
  if (a.type === "completed") {
    return {
      title: `${a.name} completó su rescate`,
      sub: "¡100% pagado!",
      icon: Trophy,
      color: "from-green-500 to-emerald-600",
    };
  }
  if (a.type === "sponsorship") {
    return {
      title: a.is_general
        ? `${a.name} patrocinó $${a.amount} al fondo general`
        : `${a.name} patrocinó $${a.amount} para ${a.target_name}`,
      sub: a.is_general ? "Fondo general · ¡Gracias!" : `Rescatando a ${a.target_name}`,
      icon: Heart,
      color: "from-rose-500 to-pink-600",
    };
  }
  return null;
}

export default function LiveActivityFeed() {
  const [activities, setActivities] = useState([]);
  const [current, setCurrent] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const idxRef = useRef(0);

  // Fetch activities every 30s
  useEffect(() => {
    let cancel = false;
    const fetchActivity = async () => {
      try {
        const { data } = await api.get("/public/activity?limit=30");
        if (!cancel) setActivities(data.activities || []);
      } catch (e) {
        // silent
      }
    };
    fetchActivity();
    const id = setInterval(fetchActivity, 60000); // refresh every 60s (was 30s)
    return () => { cancel = true; clearInterval(id); };
  }, []);

  // Cycle through activities
  useEffect(() => {
    if (dismissed || activities.length === 0) {
      setCurrent(null);
      return;
    }
    // First show: wait 4s after mount
    let firstTimeout, showInterval, hideTimeout;

    const showNext = () => {
      // Pick the activity at idxRef, advance
      const a = activities[idxRef.current % activities.length];
      setCurrent({ ...a, _key: Date.now() });
      idxRef.current += 1;
      // Hide after 5s
      hideTimeout = setTimeout(() => setCurrent(null), 5000);
    };

    firstTimeout = setTimeout(() => {
      showNext();
      // Then every 10s show next
      showInterval = setInterval(showNext, 10000);
    }, 3500);

    return () => {
      clearTimeout(firstTimeout);
      clearInterval(showInterval);
      clearTimeout(hideTimeout);
    };
  }, [activities, dismissed]);

  if (dismissed || !current) return null;
  const meta = buildLabel(current);
  if (!meta) return null;
  const Icon = meta.icon;

  return (
    <div className="fixed bottom-4 left-4 z-[100] pointer-events-none sm:max-w-sm w-[calc(100%-2rem)] sm:w-auto">
      <AnimatePresence mode="wait">
        <motion.div
          key={current._key}
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.9 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="pointer-events-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
          data-testid="live-activity-toast"
        >
          <div className="flex items-center gap-3 p-3 pr-10 relative">
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              {current.photo ? (
                <img
                  src={current.photo}
                  alt={current.name}
                  className="w-11 h-11 rounded-full object-cover border-2 border-orange-200"
                />
              ) : (
                <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${meta.color} flex items-center justify-center text-white font-heading text-lg`}>
                  {(current.name || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-gradient-to-br ${meta.color} border-2 border-white flex items-center justify-center`}>
                <Icon className="w-2.5 h-2.5 text-white" />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="text-sm text-black font-semibold truncate" data-testid="activity-toast-title">{meta.title}</div>
              <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1.5">
                <span className="truncate">{meta.sub}</span>
                <span className="text-gray-300">·</span>
                <span className="whitespace-nowrap">{relativeTime(current.timestamp)}</span>
              </div>
            </div>

            <button
              onClick={() => setDismissed(true)}
              data-testid="activity-toast-dismiss"
              className="absolute top-2 right-2 w-6 h-6 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 flex items-center justify-center transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          {/* Animated progress bar bottom */}
          <motion.div
            initial={{ width: "100%" }}
            animate={{ width: 0 }}
            transition={{ duration: 5, ease: "linear" }}
            className={`h-0.5 bg-gradient-to-r ${meta.color}`}
          />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
